import { Button, Input, Modal, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import AiOutputLanguageSelector, {
  AiOutputLanguage,
  getDefaultAiOutputLanguage,
} from './AiOutputLanguageSelector';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

interface RecruitmentAiPromptModalProps<T> {
  visible: boolean;
  title: string;
  description: string;
  promptLabel: string;
  placeholder: string;
  suggestions?: string[];
  initialPrompt?: string;
  initialLanguage?: AiOutputLanguage;
  generateLabel: string;
  generatingLabel: string;
  cancelLabel: string;
  reviewNote: string;
  additionalContent?: ReactNode;
  onCancel: () => void;
  onGenerate: (prompt: string, outputLanguage: AiOutputLanguage) => Promise<T>;
  onGenerated: (result: T, prompt: string) => void;
}

function RecruitmentAiPromptModal<T>({
  visible,
  title,
  description,
  promptLabel,
  placeholder,
  suggestions = [],
  initialPrompt = '',
  initialLanguage,
  generateLabel,
  generatingLabel,
  cancelLabel,
  reviewNote,
  additionalContent,
  onCancel,
  onGenerate,
  onGenerated,
}: RecruitmentAiPromptModalProps<T>) {
  const { i18n } = useTranslation();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [outputLanguage, setOutputLanguage] = useState<AiOutputLanguage>(
    () => initialLanguage || getDefaultAiOutputLanguage(i18n.language),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) setPrompt(initialPrompt);
  }, [initialPrompt, visible]);

  const handleGenerate = async () => {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) return;

    setLoading(true);
    try {
      const result = await onGenerate(cleanPrompt, outputLanguage);
      onGenerated(result, cleanPrompt);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      width={720}
      centered
      maskClosable={false}
      title={title}
      footer={null}
      onCancel={loading ? undefined : onCancel}
    >
      <Paragraph type="secondary">{description}</Paragraph>
      <LanguageRow>
        <AiOutputLanguageSelector
          value={outputLanguage}
          onChange={setOutputLanguage}
          disabled={loading}
        />
      </LanguageRow>
      {additionalContent}
      <FieldLabel>{promptLabel}</FieldLabel>
      <TextArea
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={placeholder}
        maxLength={5000}
        showCount
        rows={7}
        disabled={loading}
      />

      {suggestions.length > 0 && (
        <SuggestionArea>
          <Text type="secondary">Examples</Text>
          {suggestions.map((suggestion) => (
            <Button
              key={suggestion}
              size="small"
              disabled={loading}
              onClick={() => setPrompt(suggestion)}
            >
              {suggestion}
            </Button>
          ))}
        </SuggestionArea>
      )}

      <Footer>
        <Text type="secondary">{reviewNote}</Text>
        <Space>
          <Button disabled={loading} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <PrimaryButton loading={loading} disabled={!prompt.trim()} onClick={handleGenerate}>
            {loading ? generatingLabel : generateLabel}
          </PrimaryButton>
        </Space>
      </Footer>
    </Modal>
  );
}

export default RecruitmentAiPromptModal;

const FieldLabel = styled.div`
  margin-bottom: 8px;
  font-size: var(--txt_font_size_body);
`;

const LanguageRow = styled.div`
  margin-bottom: 20px;
`;

const SuggestionArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 20px;

  .ant-btn {
    height: auto;
    max-width: 100%;
    padding: 4px 10px;
    white-space: normal;
    text-align: left;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid var(--cl_neutral100);
`;

const PrimaryButton = styled(Button)`
  color: var(--cl_white);
  border-color: var(--cl_primary500);
  border-radius: var(--bd_radius_default);
  background-color: var(--cl_primary500);

  &:hover,
  &:focus {
    color: var(--cl_white);
    border-color: var(--cl_primary500);
    background-color: var(--cl_primary500);
  }
`;
